import { Component, Inject, Injector, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { AbstractComponent, ChatCommandTypeEnum, ChatSocketService } from 'lib-trend-core';
import { takeUntil } from 'rxjs/operators';
import * as ChatActions from '../../state/chat.actions';

/** Dialog input — absent `sectionId` creates a section, present renames it. */
export interface ChatFavoriteSectionDialogData {
  sectionId?: string;
  name?: string;
}

/**
 * Internal Chat favorite-section dialog (SPEC-2026-07-internal-chat, T-35 ·
 * RF-17/AC-12/AC-14).
 *
 * One form for both operations of FAVORITE_SECTION_UPSERT: no `sectionId`
 * creates a custom section, `sectionId` renames it. Sections are personal — the
 * command has no fan-out, so the sidebar re-reads them through the bootstrap
 * refresh dispatched on ack. The default section is immutable server-side
 * (`chat.errors.defaultSectionImmutable`) and is never opened here.
 */
@Component({
  templateUrl: './chat-favorite-section-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatFavoriteSectionDialogComponent extends AbstractComponent {

  submitting = false;

  sectionForm = new FormGroup({
    name: new FormControl<string>('', [Validators.required]),
  });

  constructor(
    injector: Injector,
    private readonly dialogRef: MatDialogRef<ChatFavoriteSectionDialogComponent>,
    private readonly store: Store,
    private readonly chatSocketService: ChatSocketService,
    @Inject(MAT_DIALOG_DATA) public readonly data: ChatFavoriteSectionDialogData,
  ) {
    super(injector);
    this.sectionForm.patchValue({ name: this.data?.name ?? '' });
  }

  get isRename(): boolean {
    return !!this.data?.sectionId;
  }

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (this.submitting) return;

    const name = (this.sectionForm.value.name ?? '').trim();
    if (!name) {
      this.alertService.error(this.t('chat.errors.sectionNameRequired'));
      return;
    }

    this.submitting = true;
    this.chatSocketService.sendCommand(ChatCommandTypeEnum.FAVORITE_SECTION_UPSERT, {
      sectionId: this.data?.sectionId,
      name,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Sections are user-scoped server state — re-bootstrap for the truth.
          this.store.dispatch(ChatActions.loadBootstrap());
          this.dialogRef.close(true);
        },
        error: (ack) => {
          this.submitting = false;
          this.alertService.error(this.resolveAckError(ack?.errorKey));
        },
      });
  }

  /** Translates a backend/synthetic ack errorKey, falling back when unknown. */
  private resolveAckError(errorKey?: string): string {
    if (errorKey) {
      const translated = this.t(errorKey);
      if (translated !== errorKey) return translated;
    }
    return this.t('chat.errors.commandFailed');
  }
}
